import { useState, useMemo } from 'react';
import NumberBall from '@/components/base/NumberBall';
import { useDrawHistory } from '@/hooks/useDrawHistory';
import { checkRank } from '@/lib/prizeRank';

interface SimResult {
  drawId: number;
  drawDate: string;
  rank: number | null;
  matchedCount: number;
  bonusMatched: boolean;
  winningNumbers: number[];
  winningBonus: number;
}

interface DrawSimulatorProps {
  myNumbers: number[];
  myBonus: number | null;
  label: string | null;
  onClose: () => void;
}

const rankLabels: Record<number, string> = {
  1: '1등',
  2: '2등',
  3: '3등',
  4: '4등',
  5: '5등',
};

const rankColors: Record<number, string> = {
  1: 'bg-accent-500 text-accent-950',
  2: 'bg-primary-500 text-white',
  3: 'bg-secondary-700 text-white',
  4: 'bg-foreground-700 text-white',
  5: 'bg-foreground-600 text-white',
};

export default function DrawSimulator({ myNumbers, myBonus, label, onClose }: DrawSimulatorProps) {
  const {
    draws,
    loading,
    error,
    incomplete,
    incompleteReason,
    loadedCount,
    retry,
  } = useDrawHistory();
  const [activeFilter, setActiveFilter] = useState<'all' | 'win'>('all');

  // 시뮬레이션은 회차 오름차순(과거 → 최신)으로 수행
  const allDraws = useMemo(() => [...draws].sort((a, b) => a.id - b.id), [draws]);

  const results = useMemo<SimResult[]>(() => {
    return allDraws.map((draw) => {
      const { rank, matchedCount, bonusMatched } = checkRank(myNumbers, draw.numbers, draw.bonus);
      return {
        drawId: draw.id,
        drawDate: draw.draw_date,
        rank,
        matchedCount,
        bonusMatched,
        winningNumbers: draw.numbers,
        winningBonus: draw.bonus,
      };
    });
  }, [allDraws, myNumbers]);

  const filteredResults = activeFilter === 'win'
    ? results.filter((r) => r.rank !== null)
    : results;

  const totalWins = results.filter((r) => r.rank !== null).length;
  const bestRank = results
    .filter((r) => r.rank !== null)
    .reduce((min, r) => (r.rank! < min ? r.rank! : min), 99);
  const rankCounts: Record<number, number> = {};
  results.forEach((r) => {
    if (r.rank !== null) {
      rankCounts[r.rank] = (rankCounts[r.rank] || 0) + 1;
    }
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative w-full max-w-2xl max-h-[85vh] bg-background-50 rounded-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-background-200">
          <div>
            <h2 className="font-heading text-lg text-foreground-800">당첨 시뮬레이션</h2>
            <p className="text-xs text-foreground-400 mt-0.5">
              {label || '내 번호'} · 전체 {loadedCount}회차 분석
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-foreground-500"></i>
          </button>
        </div>

        {/* My numbers display */}
        <div className="px-5 py-3 bg-background-50 border-b border-background-100 flex flex-wrap items-center gap-2">
          <span className="text-xs text-foreground-400">내 번호:</span>
          {myNumbers.map((num) => (
            <NumberBall key={num} number={num} size="xs" />
          ))}
          {myBonus && (
            <>
              <span className="text-xs text-foreground-300">+</span>
              <NumberBall number={myBonus} size="xs" isBonus />
            </>
          )}
        </div>

        {/* Loading / Error / Incomplete / Content */}
        {loading ? (
          <div className="flex-1 flex items-center justify-center py-12">
            <div className="text-center">
              <div className="w-10 h-10 mx-auto mb-3 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-foreground-400 text-sm">전체 회차 시뮬레이션 중...</p>
              <p className="text-foreground-300 text-xs mt-1">데이터 양에 따라 시간이 소요될 수 있어요</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex-1 flex items-center justify-center py-12 px-6">
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-accent-100 flex items-center justify-center">
                <i className="ri-error-warning-line text-xl text-accent-600"></i>
              </div>
              <p className="text-foreground-600 text-sm mb-1">시뮬레이션 데이터를 불러오지 못했습니다.</p>
              <p className="text-foreground-400 text-xs mb-4">{error}</p>
              <button
                onClick={retry}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line"></i>
                다시 시도
              </button>
            </div>
          </div>
        ) : (
          <>
            {incomplete && (
              <div className="px-5 py-2 bg-accent-50 border-b border-accent-100 flex items-start gap-2">
                <i className="ri-error-warning-line text-accent-600 text-xs mt-0.5"></i>
                <p className="text-accent-700 text-xs">
                  {incompleteReason || '일부 회차가 누락되어 전체 시뮬레이션이 완전하지 않습니다.'}
                </p>
              </div>
            )}

            {/* Summary */}
            <div className="px-5 py-3 border-b border-background-100">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <i className="ri-trophy-line text-accent-600"></i>
                  <span className="text-sm text-foreground-700">
                    총 <strong className="text-foreground-900">{totalWins}</strong>회 당첨
                  </span>
                </div>
                {bestRank < 99 && (
                  <div className="flex items-center gap-2">
                    <i className="ri-medal-line text-primary-500"></i>
                    <span className="text-sm text-foreground-700">
                      최고 <strong className="text-foreground-900">{rankLabels[bestRank]}</strong>
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-3 ml-auto">
                  {[5, 4, 3, 2, 1].map((rk) => {
                    const cnt = rankCounts[rk] || 0;
                    if (cnt === 0) return null;
                    return (
                      <span key={rk} className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${rankColors[rk]}`}>
                        {rankLabels[rk]} {cnt}회
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Filter tabs */}
            <div className="flex gap-1 px-5 py-2 border-b border-background-100">
              <button
                onClick={() => setActiveFilter('all')}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeFilter === 'all' ? 'bg-primary-100 text-primary-700' : 'text-foreground-400 hover:text-foreground-600'
                }`}
              >
                전체 ({results.length})
              </button>
              <button
                onClick={() => setActiveFilter('win')}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeFilter === 'win' ? 'bg-accent-100 text-accent-700' : 'text-foreground-400 hover:text-foreground-600'
                }`}
              >
                당첨만 ({totalWins})
              </button>
            </div>

            {/* Results list */}
            <div className="flex-1 overflow-y-auto px-5 py-2">
              {filteredResults.length === 0 ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-background-100 flex items-center justify-center">
                    <i className="ri-emotion-sad-line text-2xl text-foreground-300"></i>
                  </div>
                  <p className="text-foreground-500 text-sm">
                    {activeFilter === 'win' ? '당첨된 이력이 없어요.' : '아직 데이터가 없어요.'}
                  </p>
                </div>
              ) : (
                <div className="space-y-1">
                  {filteredResults.map((r) => (
                    <div
                      key={r.drawId}
                      className={`flex items-center gap-3 py-2.5 px-3 rounded-lg transition-colors ${
                        r.rank !== null
                          ? 'bg-accent-50/50 border border-accent-100/50'
                          : 'hover:bg-background-50'
                      }`}
                    >
                      <span className="text-xs font-mono text-foreground-300 w-16 flex-shrink-0">
                        제{r.drawId}회
                      </span>
                      <div className="flex gap-1 flex-shrink-0">
                        {r.winningNumbers.map((n) => (
                          <span
                            key={n}
                            className={`inline-flex w-5 h-5 rounded-full text-[10px] items-center justify-center font-bold ${
                              myNumbers.includes(n)
                                ? 'bg-primary-200 text-primary-800 ring-1 ring-primary-400'
                                : 'bg-background-200 text-foreground-400'
                            }`}
                          >
                            {n}
                          </span>
                        ))}
                        <span className="text-[10px] text-foreground-300 mx-0.5 flex items-center">+</span>
                        <span
                          className={`inline-flex w-5 h-5 rounded-full text-[10px] items-center justify-center font-bold ${
                            myBonus === r.winningBonus
                              ? 'bg-secondary-200 text-secondary-800 ring-1 ring-secondary-400'
                              : 'bg-background-200 text-foreground-400'
                          }`}
                        >
                          {r.winningBonus}
                        </span>
                      </div>
                      <span className="text-xs text-foreground-300 flex-shrink-0 w-20 text-right">
                        {r.drawDate}
                      </span>
                      {r.rank !== null ? (
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold ${rankColors[r.rank]} ml-auto`}>
                          {rankLabels[r.rank]}
                        </span>
                      ) : (
                        <span className="text-[10px] text-foreground-300 ml-auto">
                          {r.matchedCount}개 일치
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}